import { HTMLProps, useEffect, useMemo, type FC } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

import UploadImageDraggable from '@/assets/images/upload-draggable.svg';
import UploadImage from '@/assets/images/upload.svg';
import Button from '@/components/common/Button';
import useTranslation from '@/hooks/useTranslation';
import clsx from 'clsx';

interface DropzoneProps extends Partial<DropzoneOptions>, Pick<HTMLProps<HTMLDivElement>, 'className'> {
  hasUploadImage?: boolean;
  hasFooter?: boolean;
  heading?: string;
  onReject?: () => void;
  paragraphTranslation?:
    | 'pdfextra_convert_tool.dropzone.conversion_start_paragraph'
    | 'pdfextra_convert_tool.dropzone.compression_start_paragraph'
    | 'pdfextra_organize_tool.dropzone.start_paragraph';
}

const Dropzone: FC<DropzoneProps> = ({
  onDrop,
  multiple,
  accept,
  className,
  hasFooter,
  hasUploadImage,
  heading,
  onReject,
  paragraphTranslation = 'pdfextra_convert_tool.dropzone.conversion_start_paragraph',
}) => {
  const { getRootProps, getInputProps, isDragActive, fileRejections, open } = useDropzone({
    onDrop,
    multiple,
    accept,
    noClick: true,
    noKeyboard: true,
  });

  const { t } = useTranslation();

  useEffect(() => {
    if (fileRejections.length > 0 && onReject) {
      onReject();
    }
  }, [fileRejections, onReject]);

  const dropzoneClassName = useMemo(() => {
    let className = 'tw-border-dashed tw-transparent';

    if (isDragActive) {
      className = 'tw-border-solid tw-bg-gray-500 tw-bg-opacity-10';
      if (hasUploadImage) {
        className += ' !tw-border-ui-light-red';
      }
    }
    return className;
  }, [isDragActive, hasUploadImage]);

  return (
    <div
      {...getRootProps({
        className: `tw-border-4 tw-border-ui-red tw-rounded-[36px] tw-py-[55px] tw-px-3 tw-flex tw-flex-col tw-justify-center tw-items-center ${className} ${dropzoneClassName}`,
      })}
    >
      <input {...getInputProps()} />

      {heading && <h1 className="tw-text-2xl tw-font-bold tw-text-center tw-mb-5">{t(heading)}</h1>}
      {hasUploadImage && (
        <img
          alt="upload a file"
          className="tw-self-center tw-mb-[48px]"
          src={isDragActive ? UploadImageDraggable : UploadImage}
        />
      )}

      <Button
        className={clsx(
          'tw-w-full tw-py-3 tw-text-2xl sm:tw-w-auto sm:tw-px-14 tw-rounded-dropzone tw-font-medium tw-mb-6',
          { '!tw-bg-ui-light-red': isDragActive && hasUploadImage }
        )}
        onClick={open}
        gtag={{ name: 'button-choose-file' }}
      >
        {t('pdfextra_convert_tool.dropzone.choose_file')}
      </Button>

      <p
        className={clsx('tw-text-ui-red tw-text-xl tw-mb-6', {
          'tw-text-ui-blackish tw-text-xl tw-font-bold': hasUploadImage,
        })}
      >
        {t('pdfextra_convert_tool.dropzone.drag_file_here')}
      </p>

      <p
        className={clsx(
          'tw-max-w-[500px] tw-text-[17px] tw-leading-[26px] tw-text-center tw-text-ui-blackish dropzone-start-par',
          {
            'tw-text-ui-grey tw-font-normal': hasUploadImage,
          }
        )}
        dangerouslySetInnerHTML={{
          __html: t(paragraphTranslation),
        }}
      ></p>
    </div>
  );
};

export default Dropzone;
